import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { TempurLoveHeader } from "../components/TempurLoveHeader";
import { Template as ReviewsSPA } from "./ReviewsPageTemplate";
import { Template as UGCCarousel } from "./UGCCarouselTemplate";
import { Template as WriteReviewButton } from "./WriteReviewButtonTemplate";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader chain, which is typed with mutable reactivated props.
export const Template = (
    props: templates.ReviewsPageDocumentTemplate,
): React.JSX.Element => {
    const {
        page,
        love_root,
        show_sources,
        has_hero_content,
        hero_image_url,
        hero_content,
        footer_content,
        ugc_carousels,
        iovation_url,
    } = props;
    const chrome = React.useContext(Context).chrome;

    const headerClass = [
        "customer-review__header",
        "customer-review__header--reviews",
        show_sources ? "" : "customer-review__header--standalone",
        has_hero_content ? "" : "customer-review__header--empty",
        love_root ? "customer-review__header--tempurlove" : "",
    ]
        .filter(Boolean)
        .join(" ");

    const heroContainerClass = love_root
        ? "hero__reviews-container hero__reviews-container--tempurlove"
        : "hero__reviews-container";

    const reviewsContainerClass = love_root
        ? "l-capped-width reviews-page-container reviews-page-container--tempurlove"
        : "l-capped-width reviews-page-container";

    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            // Standalone reviews (show_sources=false) blank the site header to a
            // bare <header> and swap the footer for the reviews-standalone-footer;
            // messages render inside the SiteHeader, so suppress them too (paired
            // with build_page_chrome's include_messages=self.show_sources).
            header={show_sources ? undefined : <header></header>}
            footer={
                show_sources ? undefined : (
                    <div className="reviews-standalone-footer">
                        <div className="reviews-standalone-footer__container l-capped-width">
                            {footer_content.map((block, i) => (
                                <div
                                    key={i}
                                    className={`w-block-${block.block_type} block-${block.block_type}`}
                                >
                                    <div
                                        className="reviews-standalone-footer__block"
                                        dangerouslySetInnerHTML={{
                                            __html: block.value,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }
            messages={show_sources ? undefined : false}
        >
            {love_root && (
                <>
                    <TempurLoveHeader love_root={love_root} />
                    <div className="l-full-width">
                        <div className="l-capped-width">
                            <article className="sub-nav">
                                <nav
                                    className="breadcrumb"
                                    aria-label="Breadcrumb"
                                >
                                    <ul className="breadcrumb__list">
                                        <li className="breadcrumb__list-item">
                                            <a
                                                href={
                                                    love_root.url ?? undefined
                                                }
                                            >
                                                {love_root.title}
                                            </a>
                                        </li>
                                        <li
                                            className="breadcrumb__list-item"
                                            aria-current="page"
                                        >
                                            <a> {page.title}</a>
                                        </li>
                                    </ul>
                                </nav>
                            </article>
                        </div>
                    </div>
                </>
            )}
            <section className={heroContainerClass}>
                {hero_image_url ? (
                    <div
                        className="hero hero--reviews"
                        style={{
                            backgroundImage: `url('${hero_image_url}')`,
                        }}
                    ></div>
                ) : null}
                <div className={headerClass}>
                    <div className="customer-review__header-copy">
                        {hero_content.map((block, i) => (
                            <div
                                key={i}
                                className={`w-block-${block.block_type} block-${block.block_type}`}
                            >
                                {block.block_type === "title" ? (
                                    <div className="customer-review__header-title">
                                        {block.value}
                                    </div>
                                ) : (
                                    <div
                                        className="customer-review__header-subtitle"
                                        dangerouslySetInnerHTML={{
                                            __html: block.value,
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    {show_sources && !love_root && (
                        <WriteReviewButton brand="" product_type_whitelist="" />
                    )}
                </div>
            </section>
            {ugc_carousels.map((carousel, i) => (
                <UGCCarousel
                    key={i}
                    desc={carousel.desc}
                    block_data={carousel.block_data}
                />
            ))}
            <div
                className={reviewsContainerClass}
                role="group"
                aria-label={t`Product Reviews`}
            >
                <div className="reviews-app-container">
                    <div className="customer-review l-capped-width">
                        <ReviewsSPA {...props} />
                    </div>
                </div>
            </div>
            <IovationScripts
                iovationUrl={iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
